import { type FC } from 'react';
import { type DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import { _isNil } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils';
import cn from 'classnames';
import styles from '@/components/UI/DataTableV2/DataTableV2.module.scss';
import Tooltip from '@/components/UI/Tooltip';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { PickerDisplayFormat, usePickerCellBehavior } from '@/components/UI/DataTableV2/hooks/usePickerCellBehavior';
import { useAutoInsertOnActivate } from '@/components/UI/DataTableV2/hooks/useAutoInsertOnActivate';

type SampleDoseTimeCellProps = {
  coordinate: DataTableCellIndexCoordinate;
  positionOffset: { x: number; y: number };
};

export const SampleDoseTimeCell: FC<SampleDoseTimeCellProps> = ({ coordinate, positionOffset }) => {
  const { cell, picker, mouseHandlers } = usePickerCellBehavior({
    coordinate,
    positionOffset,
    displayFormat: PickerDisplayFormat.DateTimeHoursMinutesSeconds,
  });

  useAutoInsertOnActivate({
    coordinate,
    getValue: () => DateUtils.dateTimeNow(),
  });

  return (
    <div
      className={cn(styles.CellContainer, { [styles.groupBorder]: cell.hasGroupBorder })}
      style={picker.positionStyle}
      onMouseDown={mouseHandlers.onMouseDown}
      onMouseEnter={mouseHandlers.onMouseEnter}
      onMouseUp={mouseHandlers.onMouseUp}
      onDoubleClick={picker.activate}
    >
      {cell.state === 'Loading' ? (
        <div className={cn(styles.Cell, styles.Loading)} />
      ) : (
        <Tooltip render={cell.tooltip}>
          <div
            className={cn(
              styles.Cell,
              styles[cell.selectionState],
              { [styles.Active]: picker.active },
              styles[cell.state]
            )}
            data-state={cell.state}
          >
            <span className={styles.TimestampValue}>
              {_isNil(cell.value) ? (
                <span className="link blue">+ Record</span>
              ) : (
                <DateTimeRenderer value={cell.value} defaultResponse={cell.value} />
              )}
            </span>
            <input
              className={styles.TimestampInput}
              style={{ visibility: picker.active ? 'visible' : 'hidden' }}
              ref={picker.inputRef}
              type="datetime-local"
              onChange={picker.handleInputChange}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
};
